import type { Feature } from '@coffee-to-go/common';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingScreen } from '@/components/Layout/LoadingScreen';
import {
  MuiBox,
  MuiButton,
  MuiDivider,
  MuiFormLabel,
  MuiMenuItem,
  MuiSelect,
  MuiTextField,
  MuiToggleButton,
  MuiToggleButtonGroup,
  MuiTypography,
} from '@/components/mui';
import { UsageGraph } from '@/components/Stats/UsageGraph';
import { useAuth } from '@/lib/authClient';

const FEATURES = ['tgpds', 'competition'] as const;

export const Route = createFileRoute('/stats')({
  component: RouteComponent,
});

function getPastDate(days: number) {
  return new Date(new Date().setDate(new Date().getDate() - days));
}

function formatDateForInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function startOfDay(date: Date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date: Date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function startOfWeekMonday(date: Date) {
  const d = startOfDay(date);
  const day = d.getDay();
  const diff = (day + 6) % 7; // 0 for Monday, 6 for Sunday
  d.setDate(d.getDate() - diff);
  return d;
}

function endOfWeekSunday(date: Date) {
  const startMonday = startOfWeekMonday(date);
  const sunday = new Date(startMonday);
  sunday.setDate(sunday.getDate() + 6);
  return endOfDay(sunday);
}

function RouteComponent() {
  const { isAuthenticated, isPending } = useAuth();
  const navigate = useNavigate();

  const [to, setTo] = useState(new Date());
  const [from, setFrom] = useState(getPastDate(30));
  const [usageLevel, setUsageLevel] = useState<'normal' | 'power_user'>('normal');
  const [feature, setFeature] = useState<Feature>('tgpds');

  if (isPending) {
    return <LoadingScreen message="Loading app..." />;
  }

  if (!isAuthenticated) {
    navigate({ to: '/login' });
    return;
  }

  const today = new Date();
  const ninetyDaysAgo = new Date();
  ninetyDaysAgo.setDate(today.getDate() - 90);

  function clampDate(date: Date) {
    if (date < ninetyDaysAgo) return ninetyDaysAgo;
    if (date > today) return today;
    return date;
  }

  const normalizedDayFrom = startOfDay(from);
  const normalizedDayTo = endOfDay(to);
  const normalizedWeekFrom = startOfWeekMonday(from);
  const normalizedWeekTo = endOfWeekSunday(to);

  return (
    <AppLayout>
      <MuiBox sx={{ flex: 1, minHeight: 0, overflow: 'auto', maxHeight: 'calc(100vh - 64px)' }}>
        <MuiBox sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
          <MuiTypography variant="h1" sx={{ mb: 2 }}>
            Coffee Usage Stats
          </MuiTypography>

          <MuiBox sx={{ display: 'flex', gap: 2, alignItems: 'center', my: 2, flexWrap: 'wrap' }}>
            <MuiTextField
              label="From"
              type="date"
              size="small"
              value={formatDateForInput(from)}
              onChange={(e) => {
                const next = new Date(e.target.value);
                if (Number.isNaN(next.getTime())) return;
                const clamped = clampDate(next);
                if (clamped > to) {
                  setTo(clampDate(clamped));
                }
                setFrom(clamped);
              }}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: {
                  min: formatDateForInput(ninetyDaysAgo),
                  max: formatDateForInput(today),
                },
              }}
            />
            <MuiTextField
              label="To"
              type="date"
              size="small"
              value={formatDateForInput(to)}
              onChange={(e) => {
                const next = new Date(e.target.value);
                if (Number.isNaN(next.getTime())) return;
                const clamped = clampDate(next);
                if (clamped < from) {
                  setFrom(clampDate(clamped));
                }
                setTo(clamped);
              }}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: {
                  min: formatDateForInput(ninetyDaysAgo),
                  max: formatDateForInput(today),
                },
              }}
            />
            <MuiButton
              variant="contained"
              color="inherit"
              onClick={() => {
                setFrom(getPastDate(7));
                setTo(new Date());
              }}
            >
              Past week
            </MuiButton>
            <MuiButton
              variant="contained"
              color="inherit"
              onClick={() => {
                setFrom(getPastDate(30));
                setTo(new Date());
              }}
            >
              Past 30 days
            </MuiButton>
            <MuiButton
              variant="contained"
              color="inherit"
              onClick={() => {
                setFrom(getPastDate(90));
                setTo(new Date());
              }}
            >
              Past 90 days
            </MuiButton>
          </MuiBox>

          <MuiBox sx={{ mt: 2 }}>
            <MuiTypography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2, textTransform: 'uppercase' }}>
              Overall Usage
            </MuiTypography>
            <MuiDivider sx={{ my: 1.5 }} />
            <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <MuiFormLabel>Usage level</MuiFormLabel>
              <MuiToggleButtonGroup
                size="small"
                exclusive
                value={usageLevel}
                onChange={(_e, val) => {
                  if (val != null) setUsageLevel(val);
                }}
                aria-label="Usage level"
              >
                <MuiToggleButton value="normal">Any (1+ events)</MuiToggleButton>
                <MuiToggleButton value="power_user">Power user (20+ events)</MuiToggleButton>
              </MuiToggleButtonGroup>
            </MuiBox>
            <MuiBox sx={{ display: 'flex', gap: 2, flexDirection: 'row', flexWrap: 'wrap' }}>
              <MuiBox>
                <MuiTypography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                  By day (skip weekends)
                </MuiTypography>
                <UsageGraph from={normalizedDayFrom} to={normalizedDayTo} minCount={usageLevel === 'normal' ? 1 : 20} aggregateBy={'day'} />
              </MuiBox>
              <MuiBox>
                <MuiTypography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                  By week
                </MuiTypography>
                <UsageGraph
                  from={normalizedWeekFrom}
                  to={normalizedWeekTo}
                  minCount={usageLevel === 'normal' ? 1 : 50}
                  aggregateBy={'week'}
                />
              </MuiBox>
            </MuiBox>
          </MuiBox>

          <MuiBox sx={{ mt: 4 }}>
            <MuiTypography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2, textTransform: 'uppercase' }}>
              Feature Usage
            </MuiTypography>
            <MuiDivider sx={{ my: 1.5 }} />
            <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <MuiFormLabel id="feature-select-label">Feature</MuiFormLabel>
              <MuiSelect
                size="small"
                value={feature}
                onChange={(e) => setFeature(e.target.value as Feature)}
                aria-labelledby="feature-select-label"
              >
                {FEATURES.map((f) => (
                  <MuiMenuItem key={f} value={f}>
                    {f}
                  </MuiMenuItem>
                ))}
              </MuiSelect>
            </MuiBox>
            <MuiBox sx={{ display: 'flex', gap: 2, flexDirection: 'row', flexWrap: 'wrap' }}>
              <MuiBox>
                <MuiTypography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                  By day (skip weekends)
                </MuiTypography>
                <UsageGraph from={normalizedDayFrom} to={normalizedDayTo} minCount={1} aggregateBy={'day'} feature={feature} />
              </MuiBox>
              <MuiBox>
                <MuiTypography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                  By week
                </MuiTypography>
                <UsageGraph from={normalizedWeekFrom} to={normalizedWeekTo} minCount={1} aggregateBy={'week'} feature={feature} />
              </MuiBox>
            </MuiBox>
          </MuiBox>
        </MuiBox>
      </MuiBox>
    </AppLayout>
  );
}
